/**
 * PostHog session replay path allowlist (D8 Option C).
 *
 * Init keeps `disable_session_recording: true`; the client gate starts/stops
 * recording only on these routes so Clarity + PostHog coexist without recording
 * every page.
 */

/** Exact pathnames that should record (normalized, no trailing slash except `/`). */
const EXACT_PATHS = new Set([
  "/",
  "/login",
  "/signup",
  "/verify-email",
  "/verify-account",
  "/callback",
  "/forgot-password",
  "/reset-password",
  "/complete-profile",
]);

/** Prefixes (path === prefix or path starts with `${prefix}/`). */
const PREFIX_PATHS = [
  "/market",
  "/sell",
  "/item", // listing detail + payment-success routes
  "/profile", // near-me, contact, notification prefs
  "/my-items", // legacy list; redirects into the hub
  "/my-stuff", // selling, buying, looking
  "/onboarding",
  "/admin/products", // status changes + listing improve modals
] as const;

/** Strip query/hash and trailing slash (except root). */
export function normalizePathname(pathname: string | null | undefined): string {
  if (!pathname) return "/";
  const bare = pathname.split("?")[0]?.split("#")[0] ?? "/";
  if (bare.length > 1 && bare.endsWith("/")) {
    return bare.slice(0, -1);
  }
  return bare || "/";
}

function matchesPrefix(pathname: string, prefix: string): boolean {
  return pathname === prefix || pathname.startsWith(`${prefix}/`);
}

/** Whether PostHog session replay should be active for this App Router pathname. */
export function shouldRecordPostHogSession(
  pathname: string | null | undefined,
): boolean {
  const path = normalizePathname(pathname);
  if (EXACT_PATHS.has(path)) return true;
  return PREFIX_PATHS.some((prefix) => matchesPrefix(path, prefix));
}
